import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Bot,
  Zap,
  CalendarX,
  FileQuestion,
  Search,
  Receipt,
  UserPlus,
  FileCheck,
  Phone,
  AlertTriangle,
  Clock,
  CalendarClock,
  DollarSign,
  Landmark,
  ClipboardCheck,
  ChevronDown,
  ChevronUp,
  Filter,
  CheckCircle2,
  XCircle,
  SkipForward,
  Mail,
  MessageSquare,
  Bell,
  Send,
  Sparkles,
  Activity,
  FlaskConical,
  type LucideIcon,
} from "lucide-react";
import { automationsApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";

// ── Types ───────────────────────────────────────────────────────────────────

interface RuleConfig {
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  category: "client" | "trigger";
  enabled: boolean;
  autoReply: boolean;
  notifyTeam: boolean;
  template: string;
  logCount: number;
  isEmergency?: boolean;
}

interface LogEntry {
  _id: string;
  ruleKey: string;
  ruleLabel: string;
  clientName: string;
  channel: "sms" | "email";
  confidence: number;
  status: "processed" | "failed" | "skipped";
  actions: string[];
  message: string;
  reasoning: string;
  response: string;
  createdAt: string;
}

interface TestResult {
  intent: string;
  confidence: number;
  reasoning: string;
  urgency: string;
  sentiment: string;
  ruleEnabled: boolean;
  wouldAutoReply: boolean;
  wouldNotify: boolean;
  suggestedResponse: string;
}

// ── Default Rules ───────────────────────────────────────────────────────────

const DEFAULT_RULES: RuleConfig[] = [
  // Client Messages
  { key: "appointment_cancellation", label: "Appointment Cancellation", description: "When a client wants to cancel or reschedule", icon: CalendarX, category: "client", enabled: true, autoReply: true, notifyTeam: true, template: "Thank you for letting us know, {{clientName}}. We'll process your cancellation request and reach out shortly to reschedule at your convenience.\n\nBest regards,\n{{firmName}}", logCount: 12, },
  { key: "document_inquiry", label: "Document Inquiry", description: "When a client asks about documents or forms needed", icon: FileQuestion, category: "client", enabled: true, autoReply: true, notifyTeam: false, template: "Hi {{clientName}},\n\nThank you for reaching out about your documents. Our team will review your request and get back to you with the information you need regarding your matter.\n\nBest regards,\n{{firmName}}", logCount: 8, },
  { key: "case_status", label: "Case Status Request", description: "When a client asks about their case progress", icon: Search, category: "client", enabled: true, autoReply: true, notifyTeam: true, template: "Hi {{clientName}},\n\nThank you for checking in on your case. {{lawyerName}} will provide you with an update on {{matterTitle}} shortly.\n\nBest regards,\n{{firmName}}", logCount: 15, },
  { key: "invoice_payment", label: "Invoice & Payment Questions", description: "When a client has billing or payment inquiries", icon: Receipt, category: "client", enabled: true, autoReply: true, notifyTeam: true, template: "Hi {{clientName}},\n\nThank you for reaching out about your billing. Our team will review your inquiry and respond with the details you need.\n\nBest regards,\n{{firmName}}", logCount: 6, },
  { key: "new_inquiry", label: "New Client Inquiry", description: "When a potential new client reaches out", icon: UserPlus, category: "client", enabled: true, autoReply: true, notifyTeam: true, template: "Thank you for contacting {{firmName}}! We appreciate your interest. A member of our team will be in touch shortly to discuss how we can help.\n\nBest regards,\n{{firmName}}", logCount: 22, },
  { key: "document_received", label: "Document Received", description: "When a client sends or mentions sending documents", icon: FileCheck, category: "client", enabled: false, autoReply: true, notifyTeam: true, template: "Hi {{clientName}},\n\nWe've received your document submission. Our team will review it and let you know if anything else is needed.\n\nBest regards,\n{{firmName}}", logCount: 3, },
  { key: "speak_lawyer", label: "Speak with Lawyer", description: "When a client specifically requests to speak with their lawyer", icon: Phone, category: "client", enabled: true, autoReply: true, notifyTeam: true, template: "Hi {{clientName}},\n\nWe've received your request. {{lawyerName}} will reach out to you as soon as possible to schedule a call.\n\nBest regards,\n{{firmName}}", logCount: 9, },
  { key: "emergency", label: "Emergency Alert", description: "When a message is flagged as urgent or an emergency", icon: AlertTriangle, category: "client", enabled: true, autoReply: true, notifyTeam: true, template: "Hi {{clientName}},\n\nWe've received your urgent message and flagged it as a priority. A team member will respond to you as quickly as possible.\n\nBest regards,\n{{firmName}}", logCount: 2, isEmergency: true, },
  // Automated Triggers
  { key: "missed_appointment", label: "Missed Appointment Follow-up", description: "Automatically follow up after a missed appointment", icon: Clock, category: "trigger", enabled: true, autoReply: true, notifyTeam: false, template: "Hi {{clientName}},\n\nWe noticed you missed your scheduled appointment. We'd love to help you reschedule at a time that works for you. Please reply to this message or call our office.\n\nBest regards,\n{{firmName}}", logCount: 5, },
  { key: "document_deadline", label: "Document Deadline Reminder", description: "Remind clients about upcoming document submission deadlines", icon: CalendarClock, category: "trigger", enabled: true, autoReply: true, notifyTeam: false, template: "Hi {{clientName}},\n\nThis is a friendly reminder that documents for {{matterTitle}} are due soon. Please ensure all required materials are submitted on time.\n\nBest regards,\n{{firmName}}", logCount: 11, },
  { key: "overdue_invoice", label: "Overdue Invoice Reminder", description: "Send reminders for unpaid invoices past their due date", icon: DollarSign, category: "trigger", enabled: false, autoReply: true, notifyTeam: true, template: "Hi {{clientName}},\n\nThis is a courtesy reminder that you have an outstanding invoice with {{firmName}}. Please arrange payment at your earliest convenience.\n\nBest regards,\n{{firmName}}", logCount: 7, },
  { key: "court_date", label: "Court Date Reminder", description: "Notify clients about upcoming court appearances", icon: Landmark, category: "trigger", enabled: true, autoReply: true, notifyTeam: true, template: "Hi {{clientName}},\n\nThis is a reminder about your upcoming court date for {{matterTitle}}. {{lawyerName}} will be in touch with details and preparation instructions.\n\nBest regards,\n{{firmName}}", logCount: 4, },
  { key: "intake_completed", label: "Intake Form Completed", description: "When a new client submits their intake form", icon: ClipboardCheck, category: "trigger", enabled: true, autoReply: true, notifyTeam: true, template: "Hi {{clientName}},\n\nThank you for completing your intake form! Our team will review your information and reach out to schedule your initial consultation.\n\nBest regards,\n{{firmName}}", logCount: 13, },
];

// ── Merge variable chips ────────────────────────────────────────────────────

const MERGE_VARS = [
  { key: "{{clientName}}", label: "Client Name" },
  { key: "{{firmName}}", label: "Firm Name" },
  { key: "{{matterTitle}}", label: "Matter Title" },
  { key: "{{lawyerName}}", label: "Lawyer Name" },
];

// ── Quick test messages ─────────────────────────────────────────────────────

const QUICK_TESTS = [
  { label: "Cancel appt", message: "Hi, I need to cancel my appointment on Thursday because I have a conflict." },
  { label: "Case status?", message: "Can you give me an update on my case? I haven't heard anything in a while." },
  { label: "Emergency", message: "URGENT: I just got served papers and need to speak with my lawyer immediately." },
  { label: "Send docs", message: "I've attached the signed documents you requested. Please confirm you received them." },
  { label: "New client", message: "Hello, I'm looking for a lawyer to help me with a real estate transaction. Do you offer free consultations?" },
  { label: "Payment", message: "I received my invoice but I have a question about the charges. Can someone explain?" },
];

// ── Sample log data ─────────────────────────────────────────────────────────

const SAMPLE_LOGS: LogEntry[] = [
  { _id: "1", ruleKey: "appointment_cancellation", ruleLabel: "Appointment Cancellation", clientName: "Sarah Mitchell", channel: "sms", confidence: 0.94, status: "processed", actions: ["Auto-replied", "Notified team"], message: "I need to cancel my appointment on Thursday because I'm feeling sick", reasoning: "Client explicitly mentions cancelling their Thursday appointment due to illness.", response: "Thank you for letting us know, Sarah. We'll cancel your Thursday appointment. Would you like to reschedule?", createdAt: "2026-04-02T10:23:00Z" },
  { _id: "2", ruleKey: "case_status", ruleLabel: "Case Status Request", clientName: "James Cooper", channel: "email", confidence: 0.87, status: "processed", actions: ["Auto-replied", "Notified team"], message: "Hi, I was wondering if there are any updates on my immigration case? It's been a few weeks.", reasoning: "Client is asking for a status update on their ongoing case.", response: "Hi James, thank you for checking in. Your lawyer will provide you with an update shortly.", createdAt: "2026-04-02T09:15:00Z" },
  { _id: "3", ruleKey: "emergency", ruleLabel: "Emergency Alert", clientName: "Diana Rossi", channel: "sms", confidence: 0.96, status: "processed", actions: ["Auto-replied", "Notified team", "Alert sent"], message: "URGENT: I just got served papers at my workplace. I need to talk to someone NOW.", reasoning: "Message contains urgent language and legal emergency indicators.", response: "We've received your urgent message and flagged it as a priority. A team member will respond immediately.", createdAt: "2026-04-01T16:45:00Z" },
  { _id: "4", ruleKey: "new_inquiry", ruleLabel: "New Client Inquiry", clientName: "Unknown", channel: "sms", confidence: 0.82, status: "processed", actions: ["Auto-replied", "Created lead"], message: "Hi, I need a lawyer for a custody dispute. Do you handle family law?", reasoning: "New potential client inquiring about legal services.", response: "Thank you for contacting us! A member of our team will be in touch shortly.", createdAt: "2026-04-01T14:30:00Z" },
  { _id: "5", ruleKey: "document_inquiry", ruleLabel: "Document Inquiry", clientName: "Mark Thompson", channel: "email", confidence: 0.71, status: "processed", actions: ["Auto-replied"], message: "What documents do I need to bring to my meeting next week?", reasoning: "Client asking about required documentation for upcoming meeting.", response: "Our team will review your request and get back to you with the information you need.", createdAt: "2026-04-01T11:20:00Z" },
  { _id: "6", ruleKey: "invoice_payment", ruleLabel: "Invoice & Payment Questions", clientName: "Lisa Park", channel: "email", confidence: 0.45, status: "skipped", actions: [], message: "Thanks for the update. Talk soon.", reasoning: "Message is a brief acknowledgement. Low confidence for billing intent.", response: "", createdAt: "2026-03-31T15:10:00Z" },
  { _id: "7", ruleKey: "speak_lawyer", ruleLabel: "Speak with Lawyer", clientName: "Robert Chen", channel: "sms", confidence: 0.91, status: "processed", actions: ["Auto-replied", "Notified team"], message: "Can I schedule a call with my lawyer? I have some concerns about the settlement offer.", reasoning: "Client specifically requesting to speak with their assigned lawyer.", response: "We've received your request. Your lawyer will reach out to schedule a call.", createdAt: "2026-03-31T10:05:00Z" },
  { _id: "8", ruleKey: "document_received", ruleLabel: "Document Received", clientName: "Amy Walsh", channel: "email", confidence: 0.88, status: "failed", actions: ["Auto-replied"], message: "Please find attached the signed affidavit and supporting documents.", reasoning: "Client submitting signed documents for their file.", response: "", createdAt: "2026-03-30T09:40:00Z" },
];

// ── Animation variants ──────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const cardVariant = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 300, damping: 30 } },
};
const slideIn = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring" as const, stiffness: 260, damping: 28 } },
};

// ── Toggle Switch ───────────────────────────────────────────────────────────

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
}> = ({ checked, onChange, size = "md", disabled }) => {
  const dims = size === "lg" ? { w: 56, h: 30, dot: 24, tx: 24 } : size === "sm" ? { w: 36, h: 20, dot: 16, tx: 16 } : { w: 44, h: 24, dot: 20, tx: 20 };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
      style={{ width: dims.w, height: dims.h, backgroundColor: checked ? "#0F5132" : "#cbd5e1" }}
    >
      <motion.span
        className="absolute rounded-full bg-white shadow-sm"
        style={{ width: dims.dot, height: dims.dot }}
        animate={{ x: checked ? dims.tx : 3 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      />
    </button>
  );
};

// ── Animated Counter ────────────────────────────────────────────────────────

const AnimatedCounter: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = "" }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let frame: number;
    const start = performance.now();
    const duration = 800;
    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return <span>{display}{suffix}</span>;
};

// ── Confidence Badge ────────────────────────────────────────────────────────

const ConfidenceBadge: React.FC<{ value: number }> = ({ value }) => {
  const pct = Math.round(value * 100);
  const color = value >= 0.8 ? "text-emerald-700 bg-emerald-50" : value >= 0.5 ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${color}`}>{pct}%</span>;
};

// ── Status Badge ────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    processed: "bg-emerald-50 text-emerald-700",
    failed: "bg-red-50 text-red-700",
    skipped: "bg-slate-100 text-slate-600",
  };
  const icons: Record<string, React.ReactNode> = {
    processed: <CheckCircle2 className="h-3 w-3" />,
    failed: <XCircle className="h-3 w-3" />,
    skipped: <SkipForward className="h-3 w-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] || styles.skipped}`}>
      {icons[status]} {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// ── Action Badge ────────────────────────────────────────────────────────────

const ActionBadge: React.FC<{ action: string }> = ({ action }) => {
  const styles: Record<string, string> = {
    "Auto-replied": "bg-blue-50 text-blue-700",
    "Notified team": "bg-purple-50 text-purple-700",
    "Created lead": "bg-emerald-50 text-emerald-700",
    "Alert sent": "bg-red-50 text-red-700",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[action] || "bg-slate-100 text-slate-600"}`}>
      {action}
    </span>
  );
};

// ── Channel Badge ───────────────────────────────────────────────────────────

const ChannelBadge: React.FC<{ channel: string }> = ({ channel }) => {
  return channel === "sms" ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700">
      <MessageSquare className="h-3 w-3" /> SMS
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">
      <Mail className="h-3 w-3" /> Email
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const AutomationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [activeTab, setActiveTab] = useState<"rules" | "activity" | "test">(
    (searchParams.get("tab") as any) || "rules"
  );
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [rules, setRules] = useState<RuleConfig[]>(DEFAULT_RULES);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ processed: 47, successRate: 94 });

  // Template modal
  const [editingRule, setEditingRule] = useState<RuleConfig | null>(null);
  const [editTemplate, setEditTemplate] = useState("");

  // Activity log
  const [logs, setLogs] = useState<LogEntry[]>(SAMPLE_LOGS);
  const [logFilter, setLogFilter] = useState({ rule: "", status: "" });
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  // Test
  const [testMessage, setTestMessage] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Load config on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [configRes, statsRes] = await Promise.all([
          automationsApi.getConfig().catch(() => null),
          automationsApi.getStats().catch(() => null),
        ]);
        if (configRes?.data) {
          if (typeof configRes.data.globalEnabled === "boolean") setGlobalEnabled(configRes.data.globalEnabled);
          if (configRes.data.rules) {
            setRules((prev) =>
              prev.map((r) => {
                const remote = configRes.data.rules.find((rr: any) => rr.key === r.key);
                return remote ? { ...r, ...remote } : r;
              })
            );
          }
        }
        if (statsRes?.data) {
          setStats({
            processed: statsRes.data.processed ?? 47,
            successRate: statsRes.data.successRate ?? 94,
          });
        }
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Load logs when switching to activity tab
  useEffect(() => {
    if (activeTab === "activity") {
      automationsApi
        .getLogs({ rule: logFilter.rule || undefined, status: logFilter.status || undefined })
        .then((res) => {
          if (res.data?.logs?.length) setLogs(res.data.logs);
        })
        .catch(() => {});
    }
  }, [activeTab, logFilter]);

  // Sync tab to URL
  useEffect(() => {
    const current = searchParams.get("tab");
    if (current !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab]);

  // Check for ruleFilter from URL (when clicking "View Logs" on a rule)
  useEffect(() => {
    const ruleFilter = searchParams.get("ruleFilter");
    if (ruleFilter) {
      setActiveTab("activity");
      setLogFilter((f) => ({ ...f, rule: ruleFilter }));
    }
  }, []);

  // Handlers
  const toggleGlobal = useCallback(async (val: boolean) => {
    setGlobalEnabled(val);
    try { await automationsApi.updateConfig({ globalEnabled: val }); } catch {}
  }, []);

  const toggleRule = useCallback(async (key: string, field: "enabled" | "autoReply" | "notifyTeam", val: boolean) => {
    setRules((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: val } : r)));
    try {
      await automationsApi.updateConfig({ rules: [{ key, [field]: val }] });
    } catch {}
  }, []);

  const saveTemplate = useCallback(async () => {
    if (!editingRule) return;
    setRules((prev) => prev.map((r) => (r.key === editingRule.key ? { ...r, template: editTemplate } : r)));
    try {
      await automationsApi.updateConfig({ rules: [{ key: editingRule.key, template: editTemplate }] });
    } catch {}
    setEditingRule(null);
  }, [editingRule, editTemplate]);

  const runTest = useCallback(async (msg?: string) => {
    const message = msg || testMessage;
    if (!message.trim()) return;
    setTestMessage(message);
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await automationsApi.test(message);
      setTestResult(res.data);
    } catch {
      // Show a mock result for demo
      const mockIntents: Record<string, TestResult> = {
        cancel: { intent: "Appointment Cancellation", confidence: 0.92, reasoning: "Client mentions cancelling their appointment.", urgency: "Low", sentiment: "Neutral", ruleEnabled: true, wouldAutoReply: true, wouldNotify: true, suggestedResponse: "Thank you for letting us know, {{clientName}}. We'll cancel your appointment. Would you like to reschedule?" },
        status: { intent: "Case Status Request", confidence: 0.88, reasoning: "Client is asking for an update on their case.", urgency: "Low", sentiment: "Neutral", ruleEnabled: true, wouldAutoReply: true, wouldNotify: true, suggestedResponse: "Thank you for checking in. Your lawyer will provide you with an update on {{matterTitle}} shortly." },
        urgent: { intent: "Emergency Alert", confidence: 0.95, reasoning: "Message contains urgent language and emergency indicators.", urgency: "High", sentiment: "Distressed", ruleEnabled: true, wouldAutoReply: true, wouldNotify: true, suggestedResponse: "We've received your urgent message and flagged it as a priority. A team member will respond immediately." },
        doc: { intent: "Document Received", confidence: 0.85, reasoning: "Client mentions sending or attaching documents.", urgency: "Low", sentiment: "Neutral", ruleEnabled: false, wouldAutoReply: false, wouldNotify: false, suggestedResponse: "We've received your document submission. Our team will review it and let you know if anything else is needed." },
      };
      const lower = message.toLowerCase();
      let mock = mockIntents.cancel;
      if (lower.includes("status") || lower.includes("update")) mock = mockIntents.status;
      else if (lower.includes("urgent") || lower.includes("emergency") || lower.includes("immediately")) mock = mockIntents.urgent;
      else if (lower.includes("doc") || lower.includes("attach") || lower.includes("send")) mock = mockIntents.doc;
      else if (lower.includes("cancel") || lower.includes("reschedule")) mock = mockIntents.cancel;
      else if (lower.includes("payment") || lower.includes("invoice") || lower.includes("bill")) {
        mock = { intent: "Invoice & Payment Questions", confidence: 0.86, reasoning: "Client has questions about billing or payment.", urgency: "Low", sentiment: "Neutral", ruleEnabled: true, wouldAutoReply: true, wouldNotify: true, suggestedResponse: "Thank you for reaching out about your billing. Our team will review your inquiry and respond shortly." };
      } else if (lower.includes("new") || lower.includes("consultation") || lower.includes("hire")) {
        mock = { intent: "New Client Inquiry", confidence: 0.83, reasoning: "Potential new client inquiring about legal services.", urgency: "Low", sentiment: "Positive", ruleEnabled: true, wouldAutoReply: true, wouldNotify: true, suggestedResponse: "Thank you for contacting us! A member of our team will be in touch shortly to discuss how we can help." };
      } else if (lower.includes("call") || lower.includes("speak") || lower.includes("talk")) {
        mock = { intent: "Speak with Lawyer", confidence: 0.89, reasoning: "Client specifically requesting to speak with their lawyer.", urgency: "Medium", sentiment: "Neutral", ruleEnabled: true, wouldAutoReply: true, wouldNotify: true, suggestedResponse: "We've received your request. Your lawyer will reach out to schedule a call." };
      }
      setTestResult(mock);
    } finally {
      setTestLoading(false);
    }
  }, [testMessage]);

  // Filtered logs
  const filteredLogs = logs.filter((l) => {
    if (logFilter.rule && l.ruleKey !== logFilter.rule) return false;
    if (logFilter.status && l.status !== logFilter.status) return false;
    return true;
  });

  const clientRules = rules.filter((r) => r.category === "client");
  const triggerRules = rules.filter((r) => r.category === "trigger");

  const tabs = [
    { key: "rules" as const, label: "Rules", icon: Zap },
    { key: "activity" as const, label: "Activity Log", icon: Activity },
    { key: "test" as const, label: "Test", icon: FlaskConical },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="h-8 w-8 border-3 border-[#0F5132] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-2rem)] flex flex-col">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/communications")}
          className="h-9 w-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors shadow-sm"
        >
          <ArrowLeft className="h-4 w-4 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Message Automations</h1>
          <p className="text-sm text-slate-500 mt-0.5">Configure AI-powered responses and automated workflows</p>
        </div>
      </div>

      {/* Master Toggle Card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative mb-6 rounded-xl overflow-hidden"
      >
        {/* Gradient border effect */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#0F5132] via-emerald-500 to-[#198754] p-[1.5px]">
          <div className="h-full w-full rounded-[10px] bg-white" />
        </div>
        <div className="relative px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0F5132] to-[#198754] flex items-center justify-center shadow-lg shadow-emerald-200">
                <Bot className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800">AI Message Automation</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  When enabled, incoming messages are analyzed by AI and automated actions are taken based on your rules.
                </p>
              </div>
            </div>
            <ToggleSwitch checked={globalEnabled} onChange={toggleGlobal} size="lg" />
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Zap className="h-4 w-4 text-amber-500" />
              <span className="font-semibold text-slate-800"><AnimatedCounter value={stats.processed} /></span> automations processed this month
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span className="font-semibold text-slate-800"><AnimatedCounter value={stats.successRate} suffix="%" /></span> success rate
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-slate-100 rounded-xl p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key ? "text-[#0F5132]" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {activeTab === tab.key && (
              <motion.div
                layoutId="activeTab"
                className="absolute inset-0 bg-white rounded-lg shadow-sm"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative flex items-center gap-2">
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* ───────── TAB: Rules ───────── */}
      <AnimatePresence mode="wait">
        {activeTab === "rules" && (
          <motion.div
            key="rules"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Client Messages Section */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-1 w-6 rounded-full bg-[#0F5132]" />
                <h3 className="text-base font-bold text-slate-800">Client Messages</h3>
                <span className="text-xs text-slate-400 ml-1">Trigger on incoming messages</span>
              </div>
              <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid gap-3">
                {clientRules.map((rule) => (
                  <RuleCard
                    key={rule.key}
                    rule={rule}
                    globalEnabled={globalEnabled}
                    onToggle={toggleRule}
                    onEditTemplate={(r) => { setEditingRule(r); setEditTemplate(r.template); }}
                    onViewLogs={(r) => { setActiveTab("activity"); setLogFilter({ rule: r.key, status: "" }); }}
                  />
                ))}
              </motion.div>
            </div>

            {/* Automated Triggers Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-1 w-6 rounded-full bg-[#198754]" />
                <h3 className="text-base font-bold text-slate-800">Automated Triggers</h3>
                <span className="text-xs text-slate-400 ml-1">Run on schedules and events</span>
              </div>
              <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid gap-3">
                {triggerRules.map((rule) => (
                  <RuleCard
                    key={rule.key}
                    rule={rule}
                    globalEnabled={globalEnabled}
                    onToggle={toggleRule}
                    onEditTemplate={(r) => { setEditingRule(r); setEditTemplate(r.template); }}
                    onViewLogs={(r) => { setActiveTab("activity"); setLogFilter({ rule: r.key, status: "" }); }}
                  />
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* ───────── TAB: Activity Log ───────── */}
        {activeTab === "activity" && (
          <motion.div
            key="activity"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Filters */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Filter className="h-4 w-4" />
                <span>Filter:</span>
              </div>
              <select
                value={logFilter.rule}
                onChange={(e) => setLogFilter((f) => ({ ...f, rule: e.target.value }))}
                className="text-sm border border-slate-200 rounded-xl px-3 py-1.5 bg-white focus:outline-none focus:border-emerald-500"
              >
                <option value="">All Rules</option>
                {rules.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
              <select
                value={logFilter.status}
                onChange={(e) => setLogFilter((f) => ({ ...f, status: e.target.value }))}
                className="text-sm border border-slate-200 rounded-xl px-3 py-1.5 bg-white focus:outline-none focus:border-emerald-500"
              >
                <option value="">All Status</option>
                <option value="processed">Processed</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </select>
              {(logFilter.rule || logFilter.status) && (
                <button
                  onClick={() => setLogFilter({ rule: "", status: "" })}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Log List */}
            <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-2">
              {filteredLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <Activity className="h-7 w-7 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-500">No activity logs found</p>
                  <p className="text-xs text-slate-400 mt-1">Automation activity will appear here</p>
                </div>
              ) : (
                filteredLogs.map((log) => (
                  <LogRow
                    key={log._id}
                    log={log}
                    expanded={expandedLog === log._id}
                    onToggle={() => setExpandedLog(expandedLog === log._id ? null : log._id)}
                    rules={rules}
                  />
                ))
              )}
            </motion.div>
          </motion.div>
        )}

        {/* ───────── TAB: Test ───────── */}
        {activeTab === "test" && (
          <motion.div
            key="test"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-3xl">
              <div className="flex items-center gap-3 mb-1">
                <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <FlaskConical className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">Test Message Classifier</h3>
                  <p className="text-sm text-slate-500">Type a sample message to see how the AI would classify and respond.</p>
                </div>
              </div>

              {/* Input */}
              <div className="mt-5">
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="Type a sample client message..."
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 resize-none transition-colors"
                />
                <button
                  onClick={() => runTest()}
                  disabled={!testMessage.trim() || testLoading}
                  className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: testLoading ? "#64748b" : "#0F5132" }}
                >
                  {testLoading ? (
                    <>
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Classifying...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Classify Message
                    </>
                  )}
                </button>
              </div>

              {/* Result */}
              <AnimatePresence>
                {testResult && (
                  <motion.div
                    variants={slideIn}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="mt-5 rounded-xl border border-slate-200 bg-slate-50/50 p-5"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Detected Intent</p>
                        <p className="text-lg font-bold text-slate-800 mt-0.5">{testResult.intent}</p>
                      </div>
                      <ConfidenceBadge value={testResult.confidence} />
                    </div>

                    <p className="text-sm text-slate-600 mb-4 italic">"{testResult.reasoning}"</p>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">Urgency:</span>
                        <span className={`font-medium ${testResult.urgency === "High" ? "text-red-600" : testResult.urgency === "Medium" ? "text-amber-600" : "text-slate-600"}`}>
                          {testResult.urgency}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">Sentiment:</span>
                        <span className="font-medium text-slate-600">{testResult.sentiment}</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">Rule Status:</span>
                        {testResult.ruleEnabled ? (
                          <span className="flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Enabled</span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-400 font-medium"><XCircle className="h-3.5 w-3.5" /> Disabled</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">Would auto-reply:</span>
                        <span className={`font-medium ${testResult.wouldAutoReply ? "text-emerald-600" : "text-slate-400"}`}>{testResult.wouldAutoReply ? "Yes" : "No"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">Would notify team:</span>
                        <span className={`font-medium ${testResult.wouldNotify ? "text-emerald-600" : "text-slate-400"}`}>{testResult.wouldNotify ? "Yes" : "No"}</span>
                      </div>
                    </div>

                    {testResult.suggestedResponse && (
                      <div className="rounded-lg bg-white border border-slate-200 p-4">
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Suggested Response</p>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{testResult.suggestedResponse}</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Quick Tests */}
              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Quick test messages</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_TESTS.map((qt) => (
                    <button
                      key={qt.label}
                      onClick={() => runTest(qt.message)}
                      disabled={testLoading}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors disabled:opacity-50"
                    >
                      {qt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ───────── Template Editor Modal ───────── */}
      <PremiumModal
        open={!!editingRule}
        onClose={() => setEditingRule(null)}
        title="Edit Response Template"
        subtitle={editingRule?.label}
        icon={<Send className="h-5 w-5 text-[#0F5132]" />}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setEditingRule(null)}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveTemplate}
              className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: "#0F5132" }}
            >
              Save Template
            </button>
          </div>
        }
      >
        {editingRule && (
          <div className="space-y-4">
            {/* Rule name (read-only) */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Rule</label>
              <p className="text-sm font-semibold text-slate-800">{editingRule.label}</p>
            </div>

            {/* Template textarea */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Auto-reply message</label>
              <textarea
                value={editTemplate}
                onChange={(e) => setEditTemplate(e.target.value)}
                rows={6}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 resize-none font-mono"
              />
            </div>

            {/* Merge variable chips */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">Merge variables</label>
              <div className="flex flex-wrap gap-2">
                {MERGE_VARS.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setEditTemplate((t) => t + v.key)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors border border-emerald-200"
                  >
                    <span className="font-mono">{v.key}</span>
                    <span className="text-emerald-500">{v.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">Preview</label>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                  {editTemplate
                    .replace(/\{\{clientName\}\}/g, "Sarah Mitchell")
                    .replace(/\{\{firmName\}\}/g, "Mitchell & Associates")
                    .replace(/\{\{matterTitle\}\}/g, "Estate Planning - Smith")
                    .replace(/\{\{lawyerName\}\}/g, "David Chen")}
                </p>
              </div>
            </div>
          </div>
        )}
      </PremiumModal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// RULE CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const RuleCard: React.FC<{
  rule: RuleConfig;
  globalEnabled: boolean;
  onToggle: (key: string, field: "enabled" | "autoReply" | "notifyTeam", val: boolean) => void;
  onEditTemplate: (rule: RuleConfig) => void;
  onViewLogs: (rule: RuleConfig) => void;
}> = ({ rule, globalEnabled, onToggle, onEditTemplate, onViewLogs }) => {
  const Icon = rule.icon;
  const isDisabled = !globalEnabled || !rule.enabled;

  return (
    <motion.div
      variants={cardVariant}
      whileHover={{ y: -2, transition: { duration: 0.15 } }}
      className={`relative bg-white rounded-xl border shadow-sm transition-all ${
        rule.isEmergency && rule.enabled
          ? "border-red-200"
          : isDisabled
          ? "border-slate-100 opacity-70"
          : "border-slate-200 hover:shadow-md"
      }`}
    >
      {/* Emergency pulse effect */}
      {rule.isEmergency && rule.enabled && (
        <div className="absolute inset-0 rounded-xl border-2 border-red-300 animate-pulse pointer-events-none" />
      )}

      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                rule.isEmergency
                  ? rule.enabled
                    ? "bg-red-100"
                    : "bg-red-50"
                  : isDisabled
                  ? "bg-slate-100"
                  : "bg-emerald-50"
              }`}
            >
              <Icon
                className={`h-5 w-5 ${
                  rule.isEmergency
                    ? rule.enabled
                      ? "text-red-600"
                      : "text-red-300"
                    : isDisabled
                    ? "text-slate-400"
                    : "text-[#0F5132]"
                }`}
              />
            </div>
            <div>
              <h4 className={`text-sm font-bold ${isDisabled ? "text-slate-400" : "text-slate-800"}`}>
                {rule.label}
              </h4>
              <p className={`text-xs mt-0.5 ${isDisabled ? "text-slate-300" : "text-slate-500"}`}>
                {rule.description}
              </p>
            </div>
          </div>
          <ToggleSwitch
            checked={rule.enabled}
            onChange={(v) => onToggle(rule.key, "enabled", v)}
            disabled={!globalEnabled}
          />
        </div>

        {/* Sub-toggles */}
        <AnimatePresence>
          {rule.enabled && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-4 ml-[52px] rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">What happens when triggered</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Send className="h-3.5 w-3.5 text-blue-500" />
                    <span>Auto-reply to client</span>
                  </div>
                  <ToggleSwitch
                    checked={rule.autoReply}
                    onChange={(v) => onToggle(rule.key, "autoReply", v)}
                    size="sm"
                    disabled={!globalEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Bell className="h-3.5 w-3.5 text-purple-500" />
                    <span>Notify team member</span>
                  </div>
                  <ToggleSwitch
                    checked={rule.notifyTeam}
                    onChange={(v) => onToggle(rule.key, "notifyTeam", v)}
                    size="sm"
                    disabled={!globalEnabled}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="mt-3 ml-[52px] flex items-center gap-3">
                <button
                  onClick={() => onEditTemplate(rule)}
                  className="text-xs font-medium text-[#0F5132] hover:text-[#146C43] transition-colors"
                >
                  Edit Response Template
                </button>
                <span className="text-slate-200">|</span>
                <button
                  onClick={() => onViewLogs(rule)}
                  className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-1"
                >
                  View Logs
                  {rule.logCount > 0 && (
                    <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
                      {rule.logCount}
                    </span>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LOG ROW COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const LogRow: React.FC<{
  log: LogEntry;
  expanded: boolean;
  onToggle: () => void;
  rules: RuleConfig[];
}> = ({ log, expanded, onToggle, rules }) => {
  const rule = rules.find((r) => r.key === log.ruleKey);
  const Icon = rule?.icon || Zap;
  const time = new Date(log.createdAt);
  const timeStr = time.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " at " + time.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  return (
    <motion.div
      variants={cardVariant}
      className={`bg-white rounded-xl border shadow-sm transition-colors ${
        expanded ? "border-[#0F5132]/30" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full px-5 py-3.5 flex items-center gap-4 text-left"
      >
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          rule?.isEmergency ? "bg-red-100" : "bg-emerald-50"
        }`}>
          <Icon className={`h-4 w-4 ${rule?.isEmergency ? "text-red-600" : "text-[#0F5132]"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800 truncate">{log.ruleLabel}</span>
            <ChannelBadge channel={log.channel} />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-slate-500">{log.clientName}</span>
            <span className="text-xs text-slate-300">-</span>
            <span className="text-xs text-slate-400">{timeStr}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <ConfidenceBadge value={log.confidence} />
          <div className="hidden sm:flex items-center gap-1.5">
            {log.actions.map((a) => (
              <ActionBadge key={a} action={a} />
            ))}
          </div>
          <StatusBadge status={log.status} />
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 border-t border-slate-100 pt-4 space-y-4">
              {/* Original Message */}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Original Message</p>
                <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100">
                  "{log.message}"
                </p>
              </div>

              {/* AI Reasoning */}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">AI Reasoning</p>
                <p className="text-sm text-slate-600 italic">{log.reasoning}</p>
              </div>

              {/* Actions & Response */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Actions Taken</p>
                  <div className="flex flex-wrap gap-1.5">
                    {log.actions.length > 0 ? (
                      log.actions.map((a) => <ActionBadge key={a} action={a} />)
                    ) : (
                      <span className="text-xs text-slate-400">None</span>
                    )}
                  </div>
                </div>
                {log.response && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Response Sent</p>
                    <p className="text-xs text-slate-600 bg-blue-50 rounded-lg p-2 border border-blue-100">
                      {log.response}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AutomationSettingsPage;
